import React from 'react';
import Sparkline from './charts/Sparkline';
import InfoTip from './InfoTip';
import { cx, Icon } from './ui';

// Big-number tile for the KPI rows: "where are we today, and which way is it
// moving", as a label, a figure, a delta chip and an optional sparkline.
//
// Props:
//   label       — metric name, one or two words
//   value       — the figure, already formatted
//   sub         — small caption under the figure
//   delta       — optional number (e.g. +12 = "+12% wk/wk"); sign picks the colour
//   deltaSuffix — units for the delta, default '% wk/wk'
//   invertDelta — true when DOWN is good (cost, latency, errors)
//   sparkline   — optional [{label, value}, ...]
//   sparkColor  — sparkline stroke
//   tone        — 'positive' | 'negative' | 'warning' tints the figure
//   icon        — optional Icon name shown beside the label
//   help        — optional detail, rendered as an InfoTip beside the label. It
//                 used to be the native `title` of the whole tile, which put ~200
//                 characters somewhere a touch or keyboard user could never
//                 reach. InfoTip is reachable by focus, tap and Escape.
export default function StatTile({
  label, value, sub,
  delta, deltaSuffix = '% wk/wk', invertDelta = false,
  sparkline, sparkColor = 'var(--accent-text)',
  tone, icon,
  span,
  help,
}) {
  const valueClass = tone === 'positive' ? 'text-ok'
    : tone === 'negative' ? 'text-danger'
    : tone === 'warning' ? 'text-warn'
    : undefined;
  const good = delta == null ? null : (invertDelta ? delta < 0 : delta > 0);
  const deltaClass = delta == null ? null : (delta === 0 ? 'flat' : good ? 'up' : 'down');

  return (
    <div className={cx('card', 'dashboard-card', 'kpi-tile', span && `dashboard-tile-${span}`)}>
      <div className="kpi-label">
        {icon && <Icon name={icon} size={14} />}
        <span>{label}</span>
        {/* `label` as the accessible name: four of these sit in one row, and four
            identical "More information" announcements say nothing. */}
        {help && <InfoTip label={label} width={320}>{help}</InfoTip>}
      </div>
      <div className={cx('kpi-value', valueClass)}>{value}</div>
      {(sub || delta != null) && (
        <div className="kpi-meta">
          {delta != null && (
            <span className={cx('kpi-delta', deltaClass)}>
              <Icon name={delta > 0 ? 'arrowUp' : delta < 0 ? 'arrowDown' : 'minus'} size={11} strokeWidth={2.25} />
              {Math.abs(delta).toFixed(0)}{deltaSuffix}
            </span>
          )}
          {sub && <span>{sub}</span>}
        </div>
      )}
      {sparkline && sparkline.length > 0 && (
        <div className="kpi-spark">
          <Sparkline data={sparkline} color={sparkColor} height={32} />
        </div>
      )}
    </div>
  );
}
